import { Component, OnDestroy, TemplateRef, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { AttachmentsBreadcrumbItem } from 'src/app/core/models/attachments-breadcrumb-item.interface';
import { Constants } from 'src/app/core/constants';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import {
  Attachment,
  AttachmentWithDetails,
} from 'src/app/core/models/project/attachment/attachment.interface';
import { StorageUsage } from 'src/app/core/models/storage-usage.interface';
import { FileService } from 'src/app/core/services/file.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ProjectAttachmentService } from 'src/app/core/services/project-attachment.service';
import { TariffService } from 'src/app/core/services/tariff.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Convertor } from 'src/app/core/utils/convertor';
import { Util } from 'src/app/core/utils/util';

@Component({
    selector: 'app-project-files',
    templateUrl: './project-files.component.html',
    styleUrl: './project-files.component.scss',
    standalone: false
})
export class ProjectFilesComponent implements OnDestroy {
  private readonly modalService = inject(NgbModal);
  private readonly projectAttachmentService = inject(ProjectAttachmentService);
  private readonly fileService = inject(FileService);
  private readonly permissionService = inject(PermissionService);
  private readonly translateService = inject(TranslateService);
  private readonly tariffService = inject(TariffService);
  private readonly toastService = inject(ToastService);
  readonly route = inject(ActivatedRoute);

  @ViewChild('dangerTpl') dangerTpl!: TemplateRef<unknown>;

  public currentFolderId$ = new BehaviorSubject<string | null>(null);

  private breadcrumbStack$ = new BehaviorSubject<
    { id: string | null; title: string }[]
  >([
    {
      id: null,
      title: 'root',
    },
  ]);

  public readonly breadcrumbs$ = this.breadcrumbStack$.asObservable().pipe(
    map((crumbs) => {
      if (crumbs.length <= 3) {
        return crumbs.map(
          (crumb, index): AttachmentsBreadcrumbItem => ({
            ...crumb,
            index,
          }),
        );
      }

      const first = crumbs[0];
      const lastTwo = crumbs.slice(-2);

      return [
        { ...first, index: 0 },
        { id: 'ellipsis', title: '...', index: -1 },
        { ...lastTwo[0], index: crumbs.length - 2 },
        { ...lastTwo[1], index: crumbs.length - 1 },
      ];
    }),
  );

  private attachmentsSubject$ = new BehaviorSubject<
    AttachmentWithDetails[] | null
  >(null);

  public readonly currentLang: string;

  public readonly storageLimitParam: Record<string, string> = {
    uk: 'сховища',
    en: 'storage',
  };

  public callbackError?: string;

  public canUploadFiles$ = this.tariffService.companyQuotas$.pipe(
    map((companyQuotas) => {
      const quotaKey = Convertor.pascalToScreamingSnakeCase(
        Constants.QuotaKeys.s3Size,
      );
      const maxStorageSizeQuota = companyQuotas.find(
        (q) => q.key === quotaKey,
      )!;

      if (!maxStorageSizeQuota) {
        return false;
      }

      return maxStorageSizeQuota.limit > maxStorageSizeQuota.used;
    }),
  );

  public readonly storageUsage$ = this.tariffService.companyQuotas$.pipe(
    map((companyQuotas): StorageUsage => {
      const quotaKey = Convertor.pascalToScreamingSnakeCase(
        Constants.QuotaKeys.s3Size,
      );
      const storageQuota = companyQuotas.find((q) => q.key === quotaKey)!;

      const limit = storageQuota?.limit ?? 0;
      const used = storageQuota?.used ?? 0;
      const available = Math.max(limit - used, 0);
      const usedPercent = limit > 0 ? Math.round((used / limit) * 1000) / 10 : 0;

      return {
        used,
        limit,
        available,
        usedPercent,
      };
    }),
    shareReplay(1),
  );

  public readonly folders$ = this.attachmentsSubject$.pipe(
    map((attachments) => {
      const folders = attachments?.filter((a) => !a.link && a.isFolder);

      return folders;
    }),
    shareReplay(1),
  );

  public readonly files$ = this.attachmentsSubject$.pipe(
    map((attachments) => {
      const files = attachments?.filter((a) => !a.link && !a.isFolder);

      if (files) {
        for (const file of files) {
          file.imageSrc = Util.getAttachmentImage(file.title);
          file.extensionName = Util.getExtensionFromFileTitle(file.title);
          file.fileActionName = Util.canBeOpenedFile(file)
            ? 'open'
            : 'download';
          file.uploadedByName = file.createdBy ?? null;
          file.uploadedAtLabel = this.formatAttachmentUploadedAt(file.createdAt);
        }
      }

      return files;
    }),
    shareReplay(1),
  );

  public readonly canEditProject$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Project,
          UserCompanyPermissions.Edit,
        ),
      ),
      shareReplay(),
    );

  public isLoading$ = combineLatest([this.folders$, this.files$]).pipe(
    map(([folders, files]) => !folders || !files),
  );

  public attachmentsPath$ = new BehaviorSubject<string>('');

  private companyId = '';
  private projectId = '';

  private initUrls$: Observable<Params>;

  private unsubscribe$ = new Subject<void>();

  constructor() {
    const route = this.route;

    this.initUrls$ = route.params.pipe(
      first(),
      tap((params) => {
        this.companyId = route.parent?.parent?.snapshot.params['id'];
        this.projectId = params['projectId'] ?? params['id'];

        this.projectAttachmentService.setAttachmentsUrl(this.projectId);

        this.attachmentsPath$.next(
          `companies/${this.companyId}/projects/${this.projectId}/files`,
        );
      }),
      shareReplay(1),
    );

    combineLatest([this.initUrls$, this.currentFolderId$])
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(([, folderId]) =>
          this.projectAttachmentService.getProjectAttachments(folderId),
        ),
        tap((attachments) => this.attachmentsSubject$.next(attachments)),
      )
      .subscribe();

    this.breadcrumbStack$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((breadcrumbs) => {
          const validCrumbs = breadcrumbs.filter((x) => x.id);
          const ids = validCrumbs.map((x) => x.id);

          let newPath = `companies/${this.companyId}/projects/${this.projectId}/files`;

          if (ids.length) {
            newPath += `/${ids.join('/')}`;
          }

          this.attachmentsPath$.next(newPath);
        }),
      )
      .subscribe();

    this.currentLang =
      this.translateService.currentLang ?? Constants.DefaultLanguage;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onUploadFile(event: CustomFile): void {
    if (!event.upload) {
      return;
    }

    const newFile = {
      id: Util.getGuidFromTitle(event.title),
      title: event.file.name,
      fileSize: event.file.size,
      companyId: this.companyId,
      uploadId: event.upload.uploadId,
      s3Bucket: event.upload.bucket,
      s3Key: event.upload.key,
      eTag: event.upload.eTag,
      versionId: event.upload.versionId,
      contentType: event.upload.contentType,
      createdAtUtc: event.upload.createdAtUtc,
    } as Attachment;

    this.addParentId(newFile);

    const updatedAttachments = this.attachmentsSubject$.getValue() ?? [];
    updatedAttachments.push(newFile);

    this.projectAttachmentService
      .createAttachment(newFile, event.upload.correlationId)
      .pipe(
        first(),
        tap((createdAttachment) => {
          const createdFile = updatedAttachments.find((attachment) => attachment.id === createdAttachment.id);

          if (createdFile) {
            createdFile.createdAt = createdAttachment.createdAt ?? null;
            createdFile.createdBy = createdAttachment.createdBy ?? null;
            createdFile.uploadedByName = createdAttachment.createdBy ?? null;
            createdFile.uploadedAtLabel = this.formatAttachmentUploadedAt(createdAttachment.createdAt);
          }

          this.attachmentsSubject$.next([...updatedAttachments]);
        }),
      )
      .subscribe();
  }

  public onDeleteFile(event: CustomFile): void {
    const id = Util.getGuidFromTitle(event.title);

    if (!id) {
      return;
    }

    const attachments = this.attachmentsSubject$
      .getValue()!
      .filter((a) => a.id !== id);

    this.projectAttachmentService
      .deleteAttachment(id)
      .pipe(
        first(),
        tap(() => this.attachmentsSubject$.next(attachments)),
      )
      .subscribe();
  }

  public createFolderInline(folderName: string): void {
    const folder: Attachment = {
      id: Util.generateGUID(),
      title: folderName,
      isFolder: true,
      fileSize: 0,
      companyId: this.companyId,
    };

    this.addParentId(folder);

    this.projectAttachmentService
      .createAttachment(folder)
      .pipe(
        first(),
        tap((createdFolder) => {
          this.attachmentsSubject$.next([
            ...this.attachmentsSubject$.getValue()!,
            createdFolder,
          ]);
        }),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public onOpenFolder(folder: AttachmentWithDetails): void {
    if (!folder) {
      return;
    }

    const stack = [...this.breadcrumbStack$.getValue(), folder];

    this.breadcrumbStack$.next(stack);
    this.currentFolderId$.next(folder.id);
  }

  public onRemoveFolder(id: string): void {
    this.deleteAttachment(id, true);
  }

  public handleFileAction(file: Attachment, action: 'open' | 'download'): void {
    const path = file.s3Key ?? `${this.attachmentsPath$.getValue()}/${Util.getFileFullName(file)}`;
    const traceOptions = {
      uploadId: file.uploadId,
      fileId: file.id,
    };

    const fileAction$: Observable<string | Blob> =
      action === 'open'
        ? this.fileService
            .getFileUrl(path, false, traceOptions)
            .pipe(tap((url) => window.open(url, '_blank')))
        : this.fileService
            .downloadFile(path, file.title, traceOptions)
            .pipe(tap((blob) => Util.saveAs(blob, file.title)));

    fileAction$
      .pipe(
        first(),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public renameFile(id: string, newFileName: string): void {
    this.projectAttachmentService
      .renameAttachment(id, newFileName)
      .pipe(
        first(),
        tap(() => {
          this.updateAttachments(id, newFileName);
        }),
      )
      .subscribe();
  }

  public deleteAttachment(id: string, isFolder = false): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: isFolder ? 'папку' : 'файл',
        en: isFolder ? 'folder' : 'file',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => {
          const attachments = this.attachmentsSubject$.getValue()!;
          const fileToDelete = attachments.find((f) => f.id === id)!;

          const restPath = isFolder ? id : Util.getFileFullName(fileToDelete);

          return this.fileService
            .delete(fileToDelete.s3Key ?? `${this.attachmentsPath$.getValue()}/${restPath}`)
            .pipe(
              first(),
              switchMap(() =>
                this.projectAttachmentService.deleteAttachment(id),
              ),
              tap(() => {
                const updatedAttachments = attachments.filter(
                  (a) => a.id !== id,
                );
                this.attachmentsSubject$.next(updatedAttachments);
              }),
            );
        }),
      )
      .subscribe();
  }

  public onRenameFolder(newFolderName: string, id: string): void {
    this.projectAttachmentService
      .renameAttachment(id, newFolderName)
      .pipe(
        first(),
        tap(() => this.updateAttachments(id, newFolderName)),
      )
      .subscribe();
  }

  public goToBreadcrumb(index: number): void {
    const stack = this.breadcrumbStack$.getValue().slice(0, index + 1);
    this.breadcrumbStack$.next(stack);

    const folderId = index === 0 ? null : stack[stack.length - 1].id;
    this.currentFolderId$.next(folderId);
  }

  public onReachedStorageLimit(): void {
    const translatedMessage = this.translateService.instant(
      'TOOLTIPS.LIMIT_REACHED',
      { entity: this.storageLimitParam[this.currentLang] },
    );

    this.callbackError = translatedMessage;

    this.toastService.show({
      template: this.dangerTpl,
      className: 'bg-danger text-light',
    });
  }

  private addParentId(attachment: Attachment): void {
    const folderId = this.currentFolderId$.getValue();

    if (folderId) {
      attachment.parentId = folderId;
    }
  }

  private updateAttachments(id: string, newName: string): void {
    const attachments = this.attachmentsSubject$.getValue()!;
    const updatedAttachment = attachments.find((a) => a.id === id)!;

    updatedAttachment.mappedTitle = newName;

    this.attachmentsSubject$.next([...attachments]);
  }

  private formatAttachmentUploadedAt(createdAt?: string | null): string | null {
    if (!createdAt) {
      return null;
    }

    return new Intl.DateTimeFormat(this.translateService.currentLang || 'en', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(createdAt));
  }
}
